import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Table2 } from 'lucide-react';
import { isRichTextEmpty, toEditorHtml } from '../../utils/richText';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeightClassName?: string;
  editorClassName?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Type here...',
  disabled = false,
  minHeightClassName = 'min-h-[120px]',
  editorClassName = ''
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const resetToolbarState = {
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    insertUnorderedList: false,
    insertOrderedList: false
  };
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    insertUnorderedList: false,
    insertOrderedList: false
  });

  const updateToolbarState = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const selection = window.getSelection();
    const anchorNode = selection?.anchorNode;
    const isInEditor = !!anchorNode && editor.contains(anchorNode);

    if (!isInEditor || disabled) {
      setActiveFormats(resetToolbarState);
      return;
    }

    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList')
    });
  }, [disabled]);

  const editorHtml = useMemo(() => toEditorHtml(value), [value]);

  useEffect(() => {
    if (!editorRef.current) return;

    if (document.activeElement === editorRef.current) return;

    if (editorRef.current.innerHTML !== editorHtml) {
      editorRef.current.innerHTML = editorHtml;
    }
  }, [editorHtml]);

  useEffect(() => {
    document.addEventListener('selectionchange', updateToolbarState);
    const editor = editorRef.current;
    editor?.addEventListener('keyup', updateToolbarState);
    editor?.addEventListener('mouseup', updateToolbarState);
    editor?.addEventListener('focus', updateToolbarState);

    updateToolbarState();

    return () => {
      document.removeEventListener('selectionchange', updateToolbarState);
      editor?.removeEventListener('keyup', updateToolbarState);
      editor?.removeEventListener('mouseup', updateToolbarState);
      editor?.removeEventListener('focus', updateToolbarState);
    };
  }, [updateToolbarState]);

  const getToolbarButtonClass = (isActive: boolean) =>
    `p-1.5 rounded text-slate-700 disabled:opacity-50 ${isActive ? 'bg-slate-300' : 'hover:bg-slate-200'}`;

  const runCommand = (command: string) => {
    if (disabled || !editorRef.current) return;

    editorRef.current.focus();
    document.execCommand(command, false);

    const updatedHtml = editorRef.current.innerHTML;
    onChange(isRichTextEmpty(updatedHtml) ? '' : updatedHtml);
    updateToolbarState();
    requestAnimationFrame(updateToolbarState);
  };

  const handleInput = () => {
    if (!editorRef.current) return;

    const updatedHtml = editorRef.current.innerHTML;
    onChange(isRichTextEmpty(updatedHtml) ? '' : updatedHtml);
  };

  const [tablePickerOpen, setTablePickerOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);

  const insertTable = () => {
    if (disabled || !editorRef.current) return;
    editorRef.current.focus();

    const rows = Math.min(Math.max(tableRows, 1), 20);
    const cols = Math.min(Math.max(tableCols, 1), 10);
    const cell = '<td>&nbsp;</td>';
    const row = `<tr>${cell.repeat(cols)}</tr>`;
    const tableHtml = `<table><tbody>${row.repeat(rows)}</tbody></table><p><br></p>`;

    document.execCommand('insertHTML', false, tableHtml);

    const updatedHtml = editorRef.current.innerHTML;
    onChange(isRichTextEmpty(updatedHtml) ? '' : updatedHtml);
    updateToolbarState();
    setTablePickerOpen(false);
  };

  return (
    <div className="rounded-lg border border-slate-300 overflow-hidden bg-white">
      <div className="flex items-center gap-1 px-2 py-2 border-b border-slate-200 bg-slate-50">
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand('bold')}
          disabled={disabled}
          className={getToolbarButtonClass(activeFormats.bold)}
          aria-pressed={activeFormats.bold}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand('italic')}
          disabled={disabled}
          className={getToolbarButtonClass(activeFormats.italic)}
          aria-pressed={activeFormats.italic}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand('underline')}
          disabled={disabled}
          className={getToolbarButtonClass(activeFormats.underline)}
          aria-pressed={activeFormats.underline}
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand('strikeThrough')}
          disabled={disabled}
          className={getToolbarButtonClass(activeFormats.strikeThrough)}
          aria-pressed={activeFormats.strikeThrough}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand('insertUnorderedList')}
          disabled={disabled}
          className={getToolbarButtonClass(activeFormats.insertUnorderedList)}
          aria-pressed={activeFormats.insertUnorderedList}
          title="Bullet list"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand('insertOrderedList')}
          disabled={disabled}
          className={getToolbarButtonClass(activeFormats.insertOrderedList)}
          aria-pressed={activeFormats.insertOrderedList}
          title="Numbered list"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <div className="relative">
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setTablePickerOpen(open => !open)}
            disabled={disabled}
            className={getToolbarButtonClass(tablePickerOpen)}
            title="Insert table"
          >
            <Table2 className="w-4 h-4" />
          </button>
          {tablePickerOpen && (
            <div className="absolute z-10 top-full left-0 mt-1 p-3 bg-white border border-slate-200 rounded-lg shadow-lg w-48">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs text-slate-500 w-10">Rows</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={tableRows}
                  onChange={(e) => setTableRows(Number(e.target.value) || 1)}
                  className="w-full px-2 py-1 text-sm border border-slate-300 rounded"
                />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <label className="text-xs text-slate-500 w-10">Cols</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={tableCols}
                  onChange={(e) => setTableCols(Number(e.target.value) || 1)}
                  className="w-full px-2 py-1 text-sm border border-slate-300 rounded"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setTablePickerOpen(false)}
                  className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={insertTable}
                  className="px-3 py-1 text-xs font-semibold text-white bg-pink-500 hover:bg-pink-600 rounded"
                >
                  Insert
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        data-placeholder={placeholder}
        className={`${minHeightClassName} px-3 py-3 text-slate-900 focus:outline-none rich-text-editor ${editorClassName}`}
        suppressContentEditableWarning
      />
    </div>
  );
}
